import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useOutletContext, useNavigate } from 'react-router-dom';
import { getPrimaryImageUrl } from '../../utils/imageUtils';
import { useNavigationTransition } from '../../hooks/useViewTransition';
import { useStoreBranding } from '../../hooks/useStoreBranding';
import useStorefrontTheme from '../../hooks/useStorefrontTheme';
import { useGA4 } from '../../contexts/GA4Context';
import { useStorefrontCurrency } from '../../contexts/StorefrontCurrencyContext';
import { addToCart } from '../../features/cart/cartSlice';
import { getStorefrontVariantAvailability } from '../../utils/storefrontVariantUtils';
import { AdPlacement, AD_PLACEMENTS } from '../../components/ads';
import { GridClassicLayout, GridMasonryLayout, MagazineLayout } from '../../components/shop-layouts';
import ProductOverlay from '../../components/ProductOverlay';
import { useToast } from '../../components/ui/Toast';
import '../../themes/edition/EditionShop.css';
import '../../themes/atelier/AtelierShop.css';
import '../../themes/gallery/GalleryShop.css';

// Helper to check if we're on a subdomain
const isOnSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.includes('.') && !hostname.startsWith('www.');
};

const ShopPage = () => {
  const { themeStyles, theme, store, premiumThemeConfig } = useOutletContext() || { themeStyles: {}, store: {} };
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { transitionTo, isNavigating } = useNavigationTransition();
  const toast = useToast();

  // Merge premium theme global styles when available
  const globalOverrides = premiumThemeConfig?.globalOverrides || {};
  const premiumColors = premiumThemeConfig?.theme?.globalSettings?.colors || {};
  const premiumFonts = premiumThemeConfig?.theme?.globalSettings?.fonts || {};
  const { trackViewItemList, trackSearch, trackSelectItem, trackAddToCart } = useGA4();
  const hasTrackedProducts = useRef(false);
  const searchInputRef = useRef(null);

  // Shop layout settings from theme config
  const isEditionTheme = premiumThemeConfig?.theme?.slug === 'edition';
  const isAtelierTheme = premiumThemeConfig?.theme?.slug === 'atelier';
  const isGalleryTheme = premiumThemeConfig?.theme?.slug === 'gallery';
  const defaultShopLayout = {
    // A stable grid keeps mixed real-world catalogues orderly. The magazine
    // layout remains available as an explicit merchant setting, but should
    // not make missing or differently sized merchant images dominate Shop.
    layoutType: 'grid-classic',
    productsPerRow: 4,
    enableProductOverlay: false,
    heroProductCount: 1,
    cardStyle: isEditionTheme ? 'edition' : isAtelierTheme ? 'atelier' : isGalleryTheme ? 'gallery' : 'standard'
  };
  const shopLayout = globalOverrides?.shopLayout?.layoutType
    ? { ...defaultShopLayout, ...globalOverrides.shopLayout }
    : defaultShopLayout;

  // Product overlay state
  const [overlayProduct, setOverlayProduct] = useState(null);

  // Check if we're on a subdomain
  const onSubdomain = isOnSubdomain();

  // Get currency formatting from context
  const { formatPrice, getCurrencyInfo, displayCurrency } = useStorefrontCurrency();
  const currencySymbol = getCurrencyInfo(displayCurrency)?.symbol || '\u20A6';

  // Update browser tab title and favicon based on store branding
  useStoreBranding(store, 'Shop');

  // SEO Meta Tags
  useEffect(() => {
    if (!store?.name) return;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[property="${name}"], meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name); document.head.appendChild(el); }
      el.content = content;
    };
    setMeta('description', `Shop all products from ${store.name}`);
    setMeta('og:title', `Shop - ${store.name}`);
    setMeta('og:description', `Browse products from ${store.name}`);
    if (store.logo) setMeta('og:image', store.logo);
    return () => {
      ['description', 'og:title', 'og:description', 'og:image'].forEach(n => {
        const el = document.querySelector(`meta[property="${n}"], meta[name="${n}"]`);
        if (el) el.remove();
      });
    };
  }, [store]);

  // Helper to generate the correct path based on subdomain vs path-based routing
  const getStorePath = (path = '') => {
    if (onSubdomain) {
      // On subdomain: use root-level paths
      return path ? `/${path}` : '/';
    } else {
      // Path-based: use /s/:slug/ format
      return path ? `/s/${store?.slug}/${path}` : `/s/${store?.slug}`;
    }
  };
  const products = useSelector(state => state.store?.products || []);
  const status = useSelector(state => state.store?.status || 'idle');
  const loading = status === 'loading';

  const [filters, setFilters] = useState({
    category: 'all',
    priceRange: 'all',
    sortBy: 'newest',
    search: ''
  });

  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('focusSearch') === '1') {
      window.setTimeout(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    }
  }, [location.search]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: 'all',
      priceRange: 'all',
      sortBy: 'newest',
      search: ''
    });
  };

  // Filter and sort products
  const safeProducts = Array.isArray(products) ? products : [];

  // Get unique categories from active products only
  const activeProducts = safeProducts.filter(p => p.status === 'ACTIVE' && !p.deleted && !p.isDeleted);
  const categories = ['all', ...new Set(activeProducts.map(product => product.category).filter(Boolean))];
  const filteredProducts = activeProducts.filter(product => {
    if (filters.category !== 'all' && product.category !== filters.category) return false;
    if (filters.search && product.name && !product.name.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.priceRange !== 'all') {
      const price = parseFloat(product.price);
      switch (filters.priceRange) {
        case 'under-50': return price < 50;
        case '50-100': return price >= 50 && price <= 100;
        case '100-200': return price >= 100 && price <= 200;
        case 'over-200': return price > 200;
        default: return true;
      }
    }
    return true;
  }).sort((a, b) => {
    switch (filters.sortBy) {
      case 'newest': return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      case 'oldest': return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      case 'price-low': return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
      case 'price-high': return (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0);
      case 'name': return (a.name || '').localeCompare(b.name || '');
      default: return 0;
    }
  }) || [];

  const hasActiveFilters = filters.search || filters.category !== 'all' || filters.priceRange !== 'all';
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / productsPerPage));
  const pagedProducts = filteredProducts.slice((currentPage - 1) * productsPerPage, currentPage * productsPerPage);

  useEffect(() => { setCurrentPage(1); }, [filters.category, filters.priceRange, filters.sortBy, filters.search]);
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [currentPage, totalPages]);

  // GA4: Track product list view when products load
  useEffect(() => {
    if (filteredProducts.length > 0 && !hasTrackedProducts.current) {
      trackViewItemList(filteredProducts, 'Shop Page');
      hasTrackedProducts.current = true;
    }
  }, [filteredProducts, trackViewItemList]);

  // GA4: Track search when user searches
  const searchTimeoutRef = useRef(null);
  useEffect(() => {
    if (filters.search) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        trackSearch(filters.search);
      }, 500); // Debounce search tracking
    }
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [filters.search, trackSearch]);

  // Check if product has variations
  // Supports both legacy array format and new variant matrix format
  const hasVariablePricing = (product) => {
    if (!product.hasVariations || !product.variations) return false;

    // New variant matrix format: { attributes: [...], variants: [...] }
    if (typeof product.variations === 'object' && !Array.isArray(product.variations)) {
      return Array.isArray(product.variations.variants) && product.variations.variants.length > 0;
    }

    // Legacy array format: [{ name: "Size", values: [...] }]
    return Array.isArray(product.variations) && product.variations.length > 0;
  };

  // Get price range for products with variations
  // Supports both legacy array format and new variant matrix format
  const getPriceRange = (product) => {
    if (!product.hasVariations || !product.variations) {
      return null;
    }

    const prices = [];

    // New variant matrix format: { attributes: [...], variants: [...] }
    if (typeof product.variations === 'object' && !Array.isArray(product.variations)) {
      const variants = product.variations.variants || [];
      variants.forEach(variant => {
        if (variant.enabled !== false) { // Include if enabled or not specified
          const price = typeof variant.price === 'number' ? variant.price : parseFloat(variant.price);
          if (!isNaN(price) && price > 0) {
            prices.push(price);
          }
        }
      });
    } else if (Array.isArray(product.variations)) {
      // Legacy array format: [{ name: "Size", values: [...] }]
      product.variations.forEach(variation => {
        if (variation.values && Array.isArray(variation.values)) {
          variation.values.forEach(v => {
            const price = typeof v.price === 'number' ? v.price : parseFloat(v.price);
            if (!isNaN(price) && price > 0) {
              prices.push(price);
            }
          });
        }
      });
    }

    // If no variation prices found, use base price
    if (prices.length === 0) {
      const basePrice = parseFloat(product.price);
      if (!isNaN(basePrice)) {
        return { min: basePrice, max: basePrice };
      }
      return null;
    }

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    return { min: minPrice, max: maxPrice };
  };

  // Price range label helper
  const priceRangeLabels = {
    'under-50': `Under ${currencySymbol}50`,
    '50-100': `${currencySymbol}50 - ${currencySymbol}100`,
    '100-200': `${currencySymbol}100 - ${currencySymbol}200`,
    'over-200': `Over ${currencySymbol}200`
  };

  // Color helpers - prefer premium theme colors over basic themeStyles
  const { resolvedTheme } = useStorefrontTheme();
  const isDark = resolvedTheme === 'dark';
  const primaryColor = globalOverrides?.colors?.primary || premiumColors?.primary || themeStyles?.primary || '#2563eb';
  const borderColor = globalOverrides?.colors?.border || premiumColors?.border || themeStyles?.border || '#e5e7eb';
  const mutedText = globalOverrides?.colors?.muted || premiumColors?.muted || themeStyles?.textMuted || '#6b7280';
  const textColor = globalOverrides?.colors?.text || premiumColors?.text || themeStyles?.text || '#111827';
  const bgAlt = isEditionTheme
    ? (globalOverrides?.colors?.background || (isDark ? '#171716' : '#ffffff'))
    : isAtelierTheme
      ? (isDark ? '#20231f' : '#fbfaf6')
    : (globalOverrides?.colors?.background || premiumColors?.background || themeStyles?.backgroundAlt || '#f9fafb');

  // In dark mode the merchant's brand primary often sits too close to
  // the dark card background (dark-on-dark = unreadable). Prices and
  // ghost-style CTA outlines need a high-contrast fallback. textColor
  // is dark-mode-aware (white in dark mode, near-black in light mode)
  // so it always contrasts with the card surface. Light mode keeps
  // the brand emphasis on prices/CTAs.
  const accentColor = isDark ? textColor : primaryColor;
  const ctaHoverBackground = isDark ? 'rgba(255, 255, 255, 0.12)' : primaryColor;
  const ctaHoverText = isDark ? textColor : '#ffffff';
  const headingFont = premiumFonts?.heading || theme?.fonts?.heading || 'inherit';
  const bodyFont = premiumFonts?.body || theme?.fonts?.primary || 'inherit';

  // Theme colors object for layout components. `priceCtaColor` is the
  // dark-mode-aware fallback for product card prices + outline CTAs
  // (uses textColor in dark mode so dark merchant brand primaries
  // don't disappear on the dark card surface).
  const themeColors = {
    primaryColor,
    priceCtaColor: accentColor,
    borderColor,
    mutedColor: mutedText,
    textColor,
    bgAlt,
    ctaHoverBackground,
    ctaHoverText,
    headingFont,
    bodyFont,
    accentColor: themeStyles?.accent || '#f59e0b'
  };

  // Product overlay handlers
  const openProductOverlay = useCallback((product) => {
    setOverlayProduct(product);
    // Update URL for shareability without navigation
    window.history.pushState(
      { productOverlay: true, slug: product.slug },
      '',
      getStorePath(`product/${product.slug}`)
    );
    document.body.style.overflow = 'hidden';
  }, []);

  const closeProductOverlay = useCallback(() => {
    setOverlayProduct(null);
    // Go back in history to restore shop URL
    window.history.back();
    document.body.style.overflow = '';
  }, []);

  // Handle browser back button for overlay
  useEffect(() => {
    const handlePopState = (e) => {
      if (overlayProduct && !e.state?.productOverlay) {
        setOverlayProduct(null);
        document.body.style.overflow = '';
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [overlayProduct]);

  // Product click handler - uses overlay or navigates based on settings
  const handleProductClick = useCallback(async (product) => {
    trackSelectItem(product, 'Shop Products');

    if (shopLayout.enableProductOverlay) {
      openProductOverlay(product);
    } else {
      await transitionTo(() => {
        navigate(getStorePath(`product/${product.slug}`));
      }, 'forward');
    }
  }, [shopLayout.enableProductOverlay, openProductOverlay, transitionTo, navigate, trackSelectItem]);

  const handleAddToCart = useCallback(async (product) => {
    try {
      await dispatch(addToCart({
        product: {
          _id: product._id || product.id,
          id: product.id || product._id,
          name: product.name,
          price: parseFloat(product.price),
          image: getPrimaryImageUrl(product),
          storeSlug: store?.slug,
          variations: null
        },
        quantity: 1
      })).unwrap();

      if (trackAddToCart) {
        trackAddToCart(product, 1);
      }
      toast.success(`${product.name} added to cart`);
    } catch (error) {
      toast.error('Could not add item to cart. Please try again.');
    }
  }, [dispatch, store?.slug, trackAddToCart, toast]);

  return (
    <>
      <style>{`
        /* Mobile Optimizations for ShopPage */
        @media (max-width: 1024px) {
          .shop-layout {
            grid-template-columns: 1fr !important;
          }
          .shop-sidebar {
            display: none !important;
          }
          .product-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }

        @media (max-width: 768px) {
          .shop-header-container {
            padding: 1rem !important;
          }
          .shop-title {
            font-size: 1.375rem !important;
          }
          .shop-count {
            font-size: 0.8rem !important;
          }
          .view-toggle-text {
            display: none;
          }
          .view-toggle-button {
            padding: 0.5rem !important;
          }
          .shop-filter-bar {
            padding: 0.875rem 1rem !important;
          }
          .shop-filter-row {
            flex-wrap: wrap !important;
          }
          .shop-search-input {
            min-width: 100% !important;
          }
          .shop-category-pills {
            order: 3 !important;
            width: 100% !important;
          }
          .products-container {
            padding: 1.5rem 1rem !important;
          }
          .product-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.875rem !important;
          }
          .product-list-image {
            width: 120px !important;
            min-width: 120px !important;
            height: 140px !important;
          }
          .product-list-content {
            padding: 1rem !important;
          }
          .product-list-title {
            font-size: 1rem !important;
          }
          .product-list-category {
            display: none;
          }
          .product-list-price {
            font-size: 1.25rem !important;
          }
          .product-list-button {
            padding: 0.5rem 1rem !important;
            font-size: 0.8rem !important;
          }
        }

        @media (max-width: 480px) {
          .shop-header-container {
            padding: 0.75rem !important;
          }
          .shop-title {
            font-size: 1.25rem !important;
          }
          .shop-filter-bar {
            padding: 0.75rem !important;
          }
          .product-grid {
            grid-template-columns: 1fr !important;
            gap: 0.75rem !important;
          }
          .product-list-item {
            flex-direction: column !important;
          }
          .product-list-image {
            width: 100% !important;
            min-width: 100% !important;
            height: 200px !important;
          }
          .product-list-content {
            padding: 1rem !important;
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .shop-category-pills::-webkit-scrollbar {
          display: none;
        }
        .shop-category-pills {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      <div className={isEditionTheme ? 'edition-shop' : isAtelierTheme ? 'atelier-shop' : isGalleryTheme ? 'gallery-shop' : ''} style={{
        minHeight: '100vh',
        backgroundColor: bgAlt,
        fontFamily: bodyFont
      }}>
        {/* Header Bar */}
        <div style={{
          backgroundColor: themeStyles?.background || 'white',
          borderBottom: `1px solid ${borderColor}`
        }}>
          <div className="shop-header-container" style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '1.25rem 1.5rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              flexWrap: 'wrap'
            }}>
              {/* Title and Count */}
              <div>
                <h1 className="shop-title" style={{
                  fontSize: '1.625rem',
                  fontWeight: '700',
                  color: textColor,
                  margin: 0,
                  fontFamily: headingFont,
                  letterSpacing: '-0.01em'
                }}>
                  All Products
                </h1>
                <p className="shop-count" style={{
                  fontSize: '0.8125rem',
                  color: mutedText,
                  margin: '0.25rem 0 0 0'
                }}>
                  {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} available
                </p>
              </div>

              {/* View Toggle */}
              <div style={{
                display: 'flex',
                gap: '0.25rem',
                padding: '0.1875rem',
                backgroundColor: bgAlt,
                borderRadius: '8px',
                border: `1px solid ${borderColor}`
              }}>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`view-toggle-button ${viewMode === 'grid' ? 'is-active' : ''}`}
                  style={{
                    padding: '0.4375rem 0.75rem',
                    border: 'none',
                    backgroundColor: viewMode === 'grid' ? 'white' : 'transparent',
                    color: viewMode === 'grid' ? primaryColor : mutedText,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontSize: '0.8125rem',
                    fontWeight: '500',
                    transition: 'all 0.15s ease',
                    boxShadow: viewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    minHeight: '36px'
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7"/>
                    <rect x="14" y="3" width="7" height="7"/>
                    <rect x="14" y="14" width="7" height="7"/>
                    <rect x="3" y="14" width="7" height="7"/>
                  </svg>
                  <span className="view-toggle-text">Grid</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`view-toggle-button ${viewMode === 'list' ? 'is-active' : ''}`}
                  style={{
                    padding: '0.4375rem 0.75rem',
                    border: 'none',
                    backgroundColor: viewMode === 'list' ? 'white' : 'transparent',
                    color: viewMode === 'list' ? primaryColor : mutedText,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontSize: '0.8125rem',
                    fontWeight: '500',
                    transition: 'all 0.15s ease',
                    boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    minHeight: '36px'
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="8" y1="6" x2="21" y2="6"/>
                    <line x1="8" y1="12" x2="21" y2="12"/>
                    <line x1="8" y1="18" x2="21" y2="18"/>
                    <line x1="3" y1="6" x2="3.01" y2="6"/>
                    <line x1="3" y1="12" x2="3.01" y2="12"/>
                    <line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                  <span className="view-toggle-text">List</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div style={{
          backgroundColor: themeStyles?.background || 'white',
          borderBottom: `1px solid ${borderColor}`
        }}>
          <div className="shop-filter-bar" style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0.875rem 1.5rem'
          }}>
            <div className="shop-filter-row" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flexWrap: 'nowrap'
            }}>
              {/* Search */}
              <div className="shop-search-input" style={{ position: 'relative', minWidth: '220px', flex: '0 0 auto' }}>
                <svg
                  style={{
                    position: 'absolute',
                    left: '0.625rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                  }}
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={mutedText}
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search products..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem 0.5rem 2.125rem',
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px',
                    fontSize: '0.8125rem',
                    outline: 'none',
                    transition: 'all 0.15s ease',
                    backgroundColor: bgAlt,
                    color: textColor,
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = primaryColor;
                    e.target.style.boxShadow = `0 0 0 3px ${primaryColor}15`;
                    e.target.style.backgroundColor = themeStyles?.background || 'white';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = borderColor;
                    e.target.style.boxShadow = 'none';
                    e.target.style.backgroundColor = bgAlt;
                  }}
                />
              </div>

              {/* Category Pills */}
              <div className="shop-category-pills" style={{
                display: 'flex',
                gap: '0.375rem',
                overflowX: 'auto',
                flex: '1 1 auto',
                minWidth: 0
              }}>
                {categories.map(cat => {
                  const isActive = filters.category === cat;
                  const label = cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1);
                  return (
                    <button
                      key={cat}
                      onClick={() => handleFilterChange('category', cat)}
                      style={{
                        padding: '0.375rem 0.875rem',
                        border: isActive ? `1px solid ${primaryColor}` : `1px solid ${borderColor}`,
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: isActive ? '600' : '400',
                        backgroundColor: isActive ? `${primaryColor}0D` : (themeStyles?.background || 'white'),
                        color: isActive ? primaryColor : mutedText,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s ease',
                        flexShrink: 0,
                        lineHeight: '1.4'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = primaryColor + '60';
                          e.currentTarget.style.color = primaryColor;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = borderColor;
                          e.currentTarget.style.color = mutedText;
                        }
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* Divider */}
              <div style={{ width: '1px', height: '24px', backgroundColor: borderColor, flexShrink: 0 }} />

              {/* Price Range */}
              <select
                value={filters.priceRange}
                onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                style={{
                  padding: '0.5rem 1.75rem 0.5rem 0.625rem',
                  border: `1px solid ${borderColor}`,
                  borderRadius: '8px',
                  fontSize: '0.8125rem',
                  backgroundColor: bgAlt,
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.15s ease',
                  color: filters.priceRange !== 'all' ? primaryColor : mutedText,
                  fontWeight: filters.priceRange !== 'all' ? '500' : '400',
                  flexShrink: 0,
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.625rem center'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryColor;
                  e.target.style.boxShadow = `0 0 0 3px ${primaryColor}15`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = borderColor;
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="all">Price</option>
                <option value="under-50">Under {currencySymbol}50</option>
                <option value="50-100">{currencySymbol}50 - {currencySymbol}100</option>
                <option value="100-200">{currencySymbol}100 - {currencySymbol}200</option>
                <option value="over-200">Over {currencySymbol}200</option>
              </select>

              {/* Sort By */}
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                style={{
                  padding: '0.5rem 1.75rem 0.5rem 0.625rem',
                  border: `1px solid ${borderColor}`,
                  borderRadius: '8px',
                  fontSize: '0.8125rem',
                  backgroundColor: bgAlt,
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.15s ease',
                  color: mutedText,
                  flexShrink: 0,
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.625rem center'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryColor;
                  e.target.style.boxShadow = `0 0 0 3px ${primaryColor}15`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = borderColor;
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>

            {/* Active Filter Chips */}
            {hasActiveFilters && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '0.625rem',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: '0.75rem', color: mutedText, fontWeight: '500' }}>Active:</span>
                {filters.search && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.25rem 0.5rem 0.25rem 0.625rem',
                    backgroundColor: `${primaryColor}0A`,
                    border: `1px solid ${primaryColor}25`,
                    borderRadius: '9999px',
                    fontSize: '0.6875rem',
                    color: primaryColor,
                    fontWeight: '500'
                  }}>
                    Search: {filters.search}
                    <span
                      onClick={() => handleFilterChange('search', '')}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </span>
                  </span>
                )}
                {filters.category !== 'all' && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.25rem 0.5rem 0.25rem 0.625rem',
                    backgroundColor: `${primaryColor}0A`,
                    border: `1px solid ${primaryColor}25`,
                    borderRadius: '9999px',
                    fontSize: '0.6875rem',
                    color: primaryColor,
                    fontWeight: '500'
                  }}>
                    {filters.category.charAt(0).toUpperCase() + filters.category.slice(1)}
                    <span
                      onClick={() => handleFilterChange('category', 'all')}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </span>
                  </span>
                )}
                {filters.priceRange !== 'all' && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.25rem 0.5rem 0.25rem 0.625rem',
                    backgroundColor: `${primaryColor}0A`,
                    border: `1px solid ${primaryColor}25`,
                    borderRadius: '9999px',
                    fontSize: '0.6875rem',
                    color: primaryColor,
                    fontWeight: '500'
                  }}>
                    {priceRangeLabels[filters.priceRange] || filters.priceRange}
                    <span
                      onClick={() => handleFilterChange('priceRange', 'all')}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </span>
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.6875rem',
                    fontWeight: '500',
                    color: '#ef4444',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Clear all
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Products Section */}
        <div className="products-container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '1.5rem 1.5rem'
        }}>
          <div className="shop-layout" style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '2rem'
          }}>
            <div className="shop-main">
              {/* Loading State */}
              {loading && (
                isAtelierTheme || isGalleryTheme ? <div className={isGalleryTheme ? 'gallery-shop__skeleton-grid' : 'atelier-shop__skeleton-grid'} aria-label="Loading products" aria-busy="true">
                  {[1, 2, 3, 4].map((item) => <div className={isGalleryTheme ? 'gallery-shop__skeleton-card' : 'atelier-shop__skeleton-card'} key={item}><span /><i /><i /></div>)}
                </div> : <div className={isEditionTheme ? 'edition-shop__list' : ''} style={{
                  textAlign: 'center',
                  padding: '4rem 0'
                }}>
                  <div style={{
                    display: 'inline-block',
                    width: '36px',
                    height: '36px',
                    border: `3px solid ${borderColor}`,
                    borderTop: `3px solid ${primaryColor}`,
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  <p style={{
                    marginTop: '1rem',
                    color: mutedText,
                    fontSize: '0.875rem'
                  }}>
                  Loading products...
                  </p>
                </div>
              )}

              {/* Products Grid View */}
              {!loading && viewMode === 'grid' && (
                <>
                  {shopLayout.layoutType === 'grid-masonry' ? (
                    <GridMasonryLayout
                      products={pagedProducts}
                      onProductClick={handleProductClick}
                      themeColors={themeColors}
                      formatPrice={formatPrice}
                      productsPerRow={shopLayout.productsPerRow}
                      cardStyle={shopLayout.cardStyle}
                      isNavigating={isNavigating}
                      onAddToCart={handleAddToCart}
                      renderAdPlacement={store?.slug ? (index) => (
                        <AdPlacement
                          storeSlug={store.slug}
                          placement={AD_PLACEMENTS.SHOP_INLINE}
                          showAdvertiseBlurb={index === 6}
                        />
                      ) : null}
                    />
                  ) : shopLayout.layoutType === 'magazine' ? (
                    <MagazineLayout
                      products={pagedProducts}
                      onProductClick={handleProductClick}
                      themeColors={themeColors}
                      formatPrice={formatPrice}
                      heroProductCount={shopLayout.heroProductCount}
                      cardStyle={shopLayout.cardStyle}
                      isNavigating={isNavigating}
                      onAddToCart={handleAddToCart}
                      renderAdPlacement={store?.slug ? (index) => (
                        <AdPlacement
                          storeSlug={store.slug}
                          placement={AD_PLACEMENTS.SHOP_INLINE}
                          showAdvertiseBlurb={index === 6}
                        />
                      ) : null}
                    />
                  ) : (
                    <GridClassicLayout
                      products={pagedProducts}
                      onProductClick={handleProductClick}
                      themeColors={themeColors}
                      formatPrice={formatPrice}
                      productsPerRow={shopLayout.productsPerRow}
                      cardStyle={shopLayout.cardStyle}
                      isNavigating={isNavigating}
                      onAddToCart={handleAddToCart}
                      // A full-width inline ad must begin after a complete
                      // responsive grid row. Twelve is divisible by each
                      // collection theme's 4 / 3 / 2 responsive columns.
                      adInterval={isEditionTheme || isAtelierTheme || isGalleryTheme ? 12 : 6}
                      renderAdPlacement={store?.slug ? (index) => (
                        <AdPlacement
                          storeSlug={store.slug}
                          placement={AD_PLACEMENTS.SHOP_INLINE}
                          showAdvertiseBlurb={index === 6}
                        />
                      ) : null}
                    />
                  )}
                </>
              )}

              {/* Products List View */}
              {!loading && viewMode === 'list' && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.875rem'
                }}>
                  {pagedProducts.map((product, index) => {
                    const availability = getStorefrontVariantAvailability(product);
                    const isOutOfStock = !availability.hasStock;

                    return (
                    <React.Fragment key={product.id || product._id}>
                      {/* Insert inline ad after every 3 products in list view */}
                      {index > 0 && index % 3 === 0 && store?.slug && (
                        <AdPlacement
                          storeSlug={store.slug}
                          placement={AD_PLACEMENTS.SHOP_INLINE}
                          showAdvertiseBlurb={index === 3}
                        />
                      )}
                      <div
                        onClick={() => handleProductClick(product)}
                        className="product-card navigation-button product-list-item"
                        style={{
                          backgroundColor: themeStyles?.background || 'white',
                          borderRadius: '12px',
                          border: `1px solid ${borderColor}`,
                          overflow: 'hidden',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          cursor: isNavigating ? 'wait' : 'pointer',
                          display: 'flex',
                          flexDirection: 'row',
                          viewTransitionName: `product-${product.id || product._id}`,
                          textDecoration: 'none',
                          color: 'inherit'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.08)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {/* Product Image */}
                        <div className="product-list-image" style={{
                          width: '200px',
                          minWidth: '200px',
                          height: '180px',
                          backgroundColor: bgAlt,
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          {getPrimaryImageUrl(product) ? (
                            <img
                              src={getPrimaryImageUrl(product)}
                              alt={product.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                opacity: isOutOfStock ? 0.5 : 1,
                                filter: isOutOfStock ? 'grayscale(40%)' : 'none'
                              }}
                            />
                          ) : (
                            <div className={isEditionTheme ? 'edition-shop__list-placeholder' : ''} style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {isEditionTheme ? <span>{product.category || 'Product'}</span> : <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={borderColor} strokeWidth="1">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                <polyline points="21 15 16 10 5 21"/>
                              </svg>}
                            </div>
                          )}
                          {/* Out of Stock Badge */}
                          {isOutOfStock && (
                            <div style={{
                              position: 'absolute',
                              top: '0.5rem',
                              left: '0.5rem',
                              backgroundColor: 'rgba(239, 68, 68, 0.92)',
                              color: 'white',
                              padding: '0.25rem 0.625rem',
                              borderRadius: '6px',
                              fontSize: '0.6875rem',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em'
                            }}>
                              Out of Stock
                            </div>
                          )}
                          {product.featured && (
                            <div style={{
                              position: 'absolute',
                              top: '0.5rem',
                              left: isOutOfStock ? 'auto' : '0.5rem',
                              right: isOutOfStock ? '0.5rem' : 'auto',
                              backgroundColor: themeStyles?.accent || '#f59e0b',
                              color: 'white',
                              padding: '0.25rem 0.625rem',
                              borderRadius: '9999px',
                              fontSize: '0.6875rem',
                              fontWeight: '600'
                            }}>
                              Featured
                            </div>
                          )}
                          {product.activeSale && !isOutOfStock && (
                            <div style={{
                              position: 'absolute',
                              top: '0.5rem',
                              right: '0.5rem',
                              backgroundColor: '#111827',
                              color: 'white',
                              padding: '0.25rem 0.625rem',
                              borderRadius: '6px',
                              fontSize: '0.6875rem',
                              fontWeight: '700',
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em'
                            }}>
                              {product.activeSale.badgeText || 'SALE'}
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="product-list-content" style={{
                          flex: 1,
                          padding: '1.25rem',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}>
                          <div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'start',
                              marginBottom: '0.375rem'
                            }}>
                              <h3 className="product-list-title" style={{
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                color: textColor,
                                lineHeight: '1.4',
                                fontFamily: headingFont,
                                margin: 0
                              }}>
                                {product.name}
                              </h3>
                              <span className="product-list-category" style={{
                                fontSize: '0.75rem',
                                color: mutedText,
                                padding: '0.1875rem 0.5rem',
                                backgroundColor: bgAlt,
                                borderRadius: '9999px',
                                textTransform: 'capitalize',
                                whiteSpace: 'nowrap',
                                marginLeft: '1rem',
                                border: `1px solid ${borderColor}`
                              }}>
                                {product.category}
                              </span>
                            </div>

                            <p style={{
                              color: mutedText,
                              fontSize: '0.875rem',
                              lineHeight: '1.5',
                              margin: '0.375rem 0 0 0',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {product.shortDescription || product.description?.substring(0, 150) + '...' || 'View product details'}
                            </p>
                          </div>

                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '0.875rem'
                          }}>
                            {hasVariablePricing(product) ? (
                              <>
                                <div>
                                  <span className="product-list-price" style={{
                                    fontSize: '1.5rem',
                                    fontWeight: '700',
                                    color: accentColor
                                  }}>
                                    {(() => {
                                      const priceRange = getPriceRange(product);
                                      if (priceRange) {
                                        if (priceRange.min === priceRange.max) {
                                          return formatPrice(priceRange.min);
                                        }
                                        return `${formatPrice(priceRange.min)} - ${formatPrice(priceRange.max)}`;
                                      }
                                      return formatPrice(product.price);
                                    })()}
                                  </span>
                                </div>
                                <button className="product-list-button" style={{
                                  padding: '0.5rem 1.25rem',
                                  border: `1px solid ${accentColor}`,
                                  backgroundColor: 'transparent',
                                  color: accentColor,
                                  borderRadius: '8px',
                                  fontSize: '0.8125rem',
                                  fontWeight: '500',
                                  minHeight: '40px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '0.375rem',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease'
                                }}
                                  type="button"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleProductClick(product);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = ctaHoverBackground;
                                    e.currentTarget.style.color = ctaHoverText;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = accentColor;
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                    <circle cx="12" cy="12" r="3"/>
                                  </svg>
                                  Select Options
                                </button>
                              </>
                            ) : (
                              <>
                                <div>
                                  <span className="product-list-price" style={{
                                    fontSize: '1.5rem',
                                    fontWeight: '700',
                                    color: accentColor
                                  }}>
                                    {formatPrice(product.price)}
                                  </span>
                                  {product.compareAtPrice && parseFloat(product.compareAtPrice) > parseFloat(product.price) && (
                                    <span style={{
                                      fontSize: '0.875rem',
                                      color: mutedText,
                                      textDecoration: 'line-through',
                                      marginLeft: '0.5rem'
                                    }}>
                                      {formatPrice(product.compareAtPrice)}
                                    </span>
                                  )}
                                </div>
                                <button
                                  className="product-list-button"
                                  style={{
                                    padding: '0.5rem 1.25rem',
                                    border: isOutOfStock ? `1px solid ${borderColor}` : `1px solid ${accentColor}`,
                                    backgroundColor: 'transparent',
                                    color: isOutOfStock ? mutedText : accentColor,
                                    borderRadius: '8px',
                                    fontSize: '0.8125rem',
                                    fontWeight: '500',
                                    cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                                    minHeight: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.375rem',
                                    transition: 'all 0.15s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isOutOfStock) {
                                      e.currentTarget.style.backgroundColor = ctaHoverBackground;
                                      e.currentTarget.style.color = ctaHoverText;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isOutOfStock) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = accentColor;
                                    }
                                  }}
                                  type="button"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (isOutOfStock) return;
                                    handleAddToCart(product);
                                  }}
                                >
                                  {isOutOfStock ? 'Out of Stock' : (
                                    <>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="9" cy="21" r="1"/>
                                        <circle cx="20" cy="21" r="1"/>
                                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                                      </svg>
                                      Add to Cart
                                    </>
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                    );
                  })}
                </div>
              )}

              {!loading && filteredProducts.length > productsPerPage && (
                <nav className="shop-pagination" aria-label="Product pages">
                  <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1} aria-label="Previous page">←</button>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => <button key={page} type="button" onClick={() => setCurrentPage(page)} aria-current={page === currentPage ? 'page' : undefined} className={page === currentPage ? 'is-current' : ''}>{page}</button>)}
                  <button type="button" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages} aria-label="Next page">→</button>
                </nav>
              )}

              {/* No Products Found - Empty State */}
              {!loading && filteredProducts.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem 1rem'
                }}>
                  {/* Shopping bag with magnifying glass SVG */}
                  <svg
                    width="96"
                    height="96"
                    viewBox="0 0 96 96"
                    fill="none"
                    style={{ margin: '0 auto 1.5rem', display: 'block' }}
                  >
                    {/* Shopping bag */}
                    <rect x="18" y="32" width="44" height="48" rx="4" stroke={borderColor} strokeWidth="2.5" fill="none"/>
                    <path d="M30 32V24C30 17.373 35.373 12 42 12H44C50.627 12 56 17.373 56 24V32" stroke={borderColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                    <line x1="30" y1="48" x2="52" y2="48" stroke={borderColor} strokeWidth="2" strokeLinecap="round"/>
                    <line x1="30" y1="56" x2="44" y2="56" stroke={borderColor} strokeWidth="2" strokeLinecap="round"/>
                    {/* Magnifying glass */}
                    <circle cx="66" cy="58" r="14" stroke={mutedText} strokeWidth="2.5" fill="white"/>
                    <line x1="76" y1="68" x2="84" y2="76" stroke={mutedText} strokeWidth="3" strokeLinecap="round"/>
                    <line x1="60" y1="58" x2="72" y2="58" stroke={mutedText} strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="66" y1="52" x2="66" y2="64" stroke={mutedText} strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    marginBottom: '0.375rem',
                    color: textColor,
                    fontFamily: theme?.fonts?.heading || 'inherit'
                  }}>
                    No products found
                  </h3>
                  <p style={{
                    color: mutedText,
                    marginBottom: '1.5rem',
                    fontSize: '0.875rem',
                    lineHeight: '1.5'
                  }}>
                    Try adjusting your filters or search terms
                  </p>
                  <button
                    onClick={clearFilters}
                    style={{
                      backgroundColor: 'transparent',
                      color: primaryColor,
                      padding: '0.625rem 1.5rem',
                      border: `1px solid ${primaryColor}`,
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.375rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = primaryColor;
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = primaryColor;
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                    Clear Filters
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar Ad moved to bottom of main content */}
            {store?.slug && (
              <div style={{ marginTop: '2rem' }}>
                <AdPlacement
                  storeSlug={store.slug}
                  placement={AD_PLACEMENTS.SHOP_SIDEBAR}
                  showAdvertiseBlurb={true}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Overlay */}
      <ProductOverlay
        isOpen={!!overlayProduct}
        onClose={closeProductOverlay}
        product={overlayProduct}
        store={store}
        themeColors={themeColors}
        formatPrice={formatPrice}
      />
    </>
  );
};

export default ShopPage;
